import { Sparkles } from "lucide-react";
import type { CSSProperties } from "react";
import { cn } from "@/lib/utils";

/**
 * Duration (in milliseconds) of the burst animation; matches
 * `--ds-sparkle-duration` in `tokens.css`. Exposed for consumers that need to
 * coordinate other UI with the burst lifecycle.
 */
export const DS_SPARKLE_DURATION_MS = 800;

const OFFSETS = [
  { tx: "-14px", ty: "-14px" },
  { tx: "14px", ty: "-14px" },
  { tx: "-14px", ty: "14px" },
  { tx: "14px", ty: "14px" },
] as const;

export type SparkleBurstProps = {
  iconClassName?: string;
};

/**
 * One-shot sparkle burst: four icons animate outward via `.ds-sparkle-particle`
 * in `tokens.css` (opacity-only fade under `prefers-reduced-motion: reduce`),
 * then hold at `opacity: 0` thanks to `animation-fill-mode: forwards`. Mount
 * once to celebrate; remount via a `key` change to replay.
 *
 * Intended to be rendered inside an `aria-hidden`, `pointer-events-none`
 * overlay. `StepsProgress` uses it internally when `animateStepTransitions`
 * is enabled; you can also drop it into your own decorative wrappers.
 */
export function SparkleBurst({ iconClassName }: SparkleBurstProps = {}) {
  return (
    <>
      {OFFSETS.map((o) => (
        <Sparkles
          className={cn(
            "ds-sparkle-particle size-3 text-primary",
            iconClassName
          )}
          key={`${o.tx}-${o.ty}`}
          style={
            // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
            {
              "--sparkle-tx": o.tx,
              "--sparkle-ty": o.ty,
            } as CSSProperties
          }
        />
      ))}
    </>
  );
}
